import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ArrowRight, BookOpen, Clock, TrendingUp, ArrowLeft, Calendar, User, Tag } from "lucide-react";
import { mockData } from "../data/mock";

const BlogSection = () => {
  const navigate = useNavigate();

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: "bg-blue-50",
        text: "text-blue-600",
        border: "border-blue-200",
        icon: "bg-blue-100",
        hover: "hover:bg-blue-100"
      },
      purple: {
        bg: "bg-purple-50", 
        text: "text-purple-600",
        border: "border-purple-200",
        icon: "bg-purple-100",
        hover: "hover:bg-purple-100"
      },
      green: {
        bg: "bg-green-50",
        text: "text-green-600", 
        border: "border-green-200",
        icon: "bg-green-100",
        hover: "hover:bg-green-100"
      },
      teal: {
        bg: "bg-teal-50",
        text: "text-teal-600",
        border: "border-teal-200", 
        icon: "bg-teal-100",
        hover: "hover:bg-teal-100"
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  const handleReadBlog = (post) => {
    console.log('handleReadBlog called with post:', post);
    console.log('Navigating to:', `/blog/${post.id}`);
    navigate(`/blog/${post.id}`);
  };

  // Get all posts and sort by date (latest first), then take only 3 latest
  const allPosts = mockData.blogCategories.flatMap(category => 
    category.posts.map(post => ({ ...post, category }))
  );
  
  // Sort by date (latest first) and get top 3
  const latestPosts = allPosts
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  return (
    <section id="blog" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="section-badge text-white mb-4 md:mb-6" style={{background: '#112b3c'}}>
            <BookOpen className="h-3 w-3" />
            Latest Insights
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-credentra-dark-teal mb-4 md:mb-6 px-4">
            Resources & Insights
          </h2>
          <p className="text-lg md:text-xl max-w-3xl mx-auto px-4 leading-relaxed">
            The latest from our knowledge hub — FICA insights, AI developments, and expert guidance
          </p>
        </div>

        {/* Latest Blog Posts Grid - Only 3 posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
          {latestPosts.map((post) => {
            const colors = getColorClasses(post.category.color);
            return (
              <div key={post.id} className="enterprise-card group cursor-pointer relative transition-all duration-300 hover:shadow-xl flex flex-col min-h-[500px] p-6" 
                   onClick={() => handleReadBlog(post)}>
                
                {/* Category Badge - Fixed positioning */}
                <div className="absolute top-4 right-4 z-10">
                  <div className={`${colors.bg} ${colors.border} border rounded-lg px-3 py-1.5 shadow-sm`}>
                    <div className="flex items-center gap-1.5">
                      <post.category.icon className={`h-3 w-3 ${colors.text}`} />
                      <span className={`text-xs font-medium ${colors.text}`}>{post.category.title}</span>
                    </div>
                  </div>
                </div>

                {/* Header Section */}
                <div className="mb-4 pr-20">
                  <div className={`w-12 h-12 md:w-14 md:h-14 ${colors.icon} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <post.category.icon className={`h-5 w-5 md:h-7 md:w-7 ${colors.text}`} />
                  </div>
                  
                  {/* Meta Information */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{post.readTime} min read</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                {/* Content Section - Takes up available space */}
                <div className="flex-grow">
                  <h3 className="text-lg md:text-xl font-bold text-credentra-dark-teal mb-3 line-clamp-2 group-hover:text-credentra-bright-teal transition-colors leading-tight">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm md:text-base leading-relaxed">
                    {post.excerpt}
                  </p>
                </div>

                {/* Footer Section - Always at bottom */}
                <div className="mt-auto">
                  {/* Tags */}
                  {post.tags && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 2).map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Author & CTA */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <User className="h-4 w-4" />
                      <span className="truncate">{post.author}</span>
                    </div>
                    <div className="flex items-center font-semibold group-hover:gap-3 transition-all duration-300 flex-shrink-0" style={{color: '#112b3c'}}>
                      <span className="text-sm">Read More</span>
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Blogs CTA */}
        <div className="text-center px-4">
          <Button 
            size="lg"
            onClick={() => navigate('/blog')}
            className="text-white font-semibold px-6 md:px-8 py-3 md:py-4 rounded-lg transition-all duration-300 hover:scale-105 w-full sm:w-auto shadow-lg hover:shadow-xl"
            style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
          >
            <BookOpen className="h-4 md:h-5 w-4 md:w-5 mr-2" />
            View All Resources
            <ArrowRight className="h-4 md:h-5 w-4 md:w-5 ml-2" />
          </Button>
        </div>

      </div>
    </section>
  );
};

export default BlogSection;